import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Calculator, Sigma, Hash, TrendingUp, CheckCircle, RotateCcw, Play } from 'lucide-react';
import { useAcademyStore } from '../../../store/academyStore';
import { useMusicStore } from '../../../store/musicStore';
import { LessonWrapper, LessonPage } from '../../ui/LessonWrapper';

// ============ LESSON CONTENT ============
const LESSONS: LessonPage[] = [
    {
        id: 1,
        title: "Squashing Data",
        icon: "🍋",
        content: (
            <div className="space-y-6">
                <div className="bg-amber-50 p-6 rounded-2xl border-2 border-amber-200">
                    <h3 className="font-bold text-xl text-amber-800 mb-3">Many Rows to One</h3>
                    <p className="text-slate-700 mb-4">
                        Sometimes you don't want to see every single sale. You just want the <strong>Total</strong>.
                    </p>
                    <p className="text-slate-700">
                        <strong>Aggregation</strong> is like taking a bag of lemons and squeezing them into one glass of juice.
                        You turn many rows into one number.
                    </p>
                </div>
            </div>
        )
    },
    {
        id: 2,
        title: "The Big Functions",
        icon: "🧮",
        content: (
            <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-orange-50 p-4 rounded-xl border border-orange-200">
                        <h3 className="font-bold text-orange-800 mb-2">SUM()</h3>
                        <p className="text-slate-600 text-sm">Adds everything up.</p>
                        <code className="text-xs bg-white px-1 rounded block mt-2">SUM(price) = $500</code>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-xl border border-blue-200">
                        <h3 className="font-bold text-blue-800 mb-2">AVG()</h3>
                        <p className="text-slate-600 text-sm">Finds the middle value.</p>
                        <code className="text-xs bg-white px-1 rounded block mt-2">AVG(score) = 85.5</code>
                    </div>
                    <div className="bg-purple-50 p-4 rounded-xl border border-purple-200">
                        <h3 className="font-bold text-purple-800 mb-2">MAX() / MIN()</h3>
                        <p className="text-slate-600 text-sm">Highest or Lowest.</p>
                        <code className="text-xs bg-white px-1 rounded block mt-2">MAX(level) = 99</code>
                    </div>
                    <div className="bg-green-50 p-4 rounded-xl border border-green-200">
                        <h3 className="font-bold text-green-800 mb-2">COUNT()</h3>
                        <p className="text-slate-600 text-sm">Counts the rows.</p>
                        <code className="text-xs bg-white px-1 rounded block mt-2">COUNT(*) = 10 users</code>
                    </div>
                </div>
            </div>
        )
    },
    {
        id: 3,
        title: "Divide and Conquer",
        icon: "📦",
        content: (
            <div className="space-y-6">
                <div className="bg-slate-50 p-6 rounded-2xl border-2 border-slate-200">
                    <h3 className="font-bold text-xl text-slate-800 mb-3">GROUP BY</h3>
                    <p className="text-slate-700 mb-4">
                        What if you want the total for <em>each</em> category separately?
                    </p>
                    <p className="text-slate-700 mb-4">
                        <strong>GROUP BY</strong> splits the data into buckets first, then squashes each bucket.
                    </p>
                    <div className="font-mono text-sm bg-slate-800 text-white p-4 rounded-lg">
                        SELECT <span className="text-green-400">category</span>, <span className="text-yellow-400">SUM(amount)</span>
                        <br />FROM sales
                        <br />GROUP BY <span className="text-green-400">category</span>
                    </div>
                </div>
            </div>
        )
    }
];

// ============ GAME COMPONENT ============
interface DataRow {
    id: number;
    category: string;
    amount: number;
    quantity: number;
}

const SAMPLE_DATA: DataRow[] = [
    { id: 1, category: 'Fruit', amount: 150, quantity: 10 },
    { id: 2, category: 'Fruit', amount: 200, quantity: 15 },
    { id: 3, category: 'Vegetable', amount: 80, quantity: 8 },
    { id: 4, category: 'Vegetable', amount: 120, quantity: 12 },
    { id: 5, category: 'Fruit', amount: 175, quantity: 11 },
    { id: 6, category: 'Meat', amount: 300, quantity: 5 },
    { id: 7, category: 'Meat', amount: 450, quantity: 7 },
    { id: 8, category: 'Vegetable', amount: 95, quantity: 9 },
];

type AggFunc = 'COUNT' | 'SUM' | 'AVG' | 'MIN' | 'MAX';

const MISSIONS = [
    {
        id: 1,
        text: "Mission 1: Calculate Total Revenue.",
        hint: "We need the SUM of all 'amount' values. Do not group them yet.",
        check: (func: AggFunc, col: string, grouped: boolean, result: any) =>
            func === 'SUM' && col === 'amount' && !grouped
    },
    {
        id: 2,
        text: "Mission 2: Find the Best Selling Category by Volume.",
        hint: "Calculate the TOTAL (SUM) 'quantity', but GROUP BY 'category' to see the breakdown.",
        check: (func: AggFunc, col: string, grouped: boolean, result: any) =>
            func === 'SUM' && col === 'quantity' && grouped
    },
    {
        id: 3,
        text: "Mission 3: Find the Most Expensive Order.",
        hint: "We need the MAX 'amount' from the entire list.",
        check: (func: AggFunc, col: string, grouped: boolean, result: any) =>
            func === 'MAX' && col === 'amount' && !grouped
    }
];

const AggregateGame = ({ onComplete }: { onComplete: () => void }) => {
    const [selectedFunc, setSelectedFunc] = useState<AggFunc>('COUNT');
    const [selectedColumn, setSelectedColumn] = useState<'amount' | 'quantity'>('amount');
    const [groupBy, setGroupBy] = useState(false);
    const [result, setResult] = useState<any>(null);

    // Mission State
    const [missionIndex, setMissionIndex] = useState(0);
    const [missionComplete, setMissionComplete] = useState(false);
    const currentMission = MISSIONS[missionIndex];

    const FUNCTIONS: { id: AggFunc; label: string; desc: string }[] = [
        { id: 'COUNT', label: 'COUNT(*)', desc: 'Number of rows' },
        { id: 'SUM', label: 'SUM(col)', desc: 'Total of all values' },
        { id: 'AVG', label: 'AVG(col)', desc: 'Average value' },
        { id: 'MIN', label: 'MIN(col)', desc: 'Smallest value' },
        { id: 'MAX', label: 'MAX(col)', desc: 'Largest value' },
    ];

    const runQuery = () => {
        const data = SAMPLE_DATA;
        let output: any;

        if (groupBy) {
            const groups: { [key: string]: DataRow[] } = {};
            data.forEach(row => {
                if (!groups[row.category]) groups[row.category] = [];
                groups[row.category].push(row);
            });

            output = Object.entries(groups).map(([category, rows]) => {
                const values = rows.map(r => r[selectedColumn]);
                let aggregated: number;

                switch (selectedFunc) {
                    case 'COUNT': aggregated = rows.length; break;
                    case 'SUM': aggregated = values.reduce((a, b) => a + b, 0); break;
                    case 'AVG': aggregated = Math.round(values.reduce((a, b) => a + b, 0) / values.length); break;
                    case 'MIN': aggregated = Math.min(...values); break;
                    case 'MAX': aggregated = Math.max(...values); break;
                }

                return { category, result: aggregated };
            });
        } else {
            const values = data.map(r => r[selectedColumn]);
            switch (selectedFunc) {
                case 'COUNT': output = data.length; break;
                case 'SUM': output = values.reduce((a, b) => a + b, 0); break;
                case 'AVG': output = Math.round(values.reduce((a, b) => a + b, 0) / values.length); break;
                case 'MIN': output = Math.min(...values); break;
                case 'MAX': output = Math.max(...values); break;
            }
        }

        setResult(output);

        // Check Mission
        if (!missionComplete && currentMission.check(selectedFunc, selectedColumn, groupBy, output)) {
            setMissionComplete(true);
            setTimeout(() => {
                if (missionIndex < MISSIONS.length - 1) {
                    setMissionIndex(prev => prev + 1);
                    setMissionComplete(false);
                    setResult(null); // Clear result for next mission
                } else {
                    onComplete();
                }
            }, 2000);
        }
    };

    // Clear result when inputs change to prevent type mismatches (e.g. mapping a number)
    useEffect(() => {
        setResult(null);
    }, [groupBy, selectedFunc, selectedColumn]);

    const getSqlQuery = () => {
        const funcPart = selectedFunc === 'COUNT' ? 'COUNT(*)' : `${selectedFunc}(${selectedColumn})`;
        let sql = `SELECT ${groupBy ? 'category, ' : ''}${funcPart}\nFROM sales`;
        if (groupBy) sql += '\nGROUP BY category';
        return sql;
    };

    return (
        <div className="space-y-8">
            <div className="text-center">
                <h1 className="text-4xl font-black font-comic text-slate-800 mb-2">
                    <Calculator className="inline text-amber-600 mr-3" size={40} />
                    The Calculator
                </h1>

                {/* Mission Banner */}
                <div className={`max-w-2xl mx-auto mb-6 p-4 rounded-xl border-2 transition-all duration-500 ${missionComplete ? 'bg-green-100 border-green-400 scale-105' : 'bg-white border-blue-400 shadow-lg'}`}>
                    <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">
                        {missionComplete ? '✅ MISSION COMPLETE!' : '🎯 CURRENT OBJECTIVE'}
                    </div>
                    <div className={`text-xl font-bold ${missionComplete ? 'text-green-700' : 'text-blue-700'}`}>
                        {currentMission.text}
                    </div>
                    {!missionComplete && (
                        <div className="text-sm text-slate-400 mt-1 italic">
                            Hint: {currentMission.hint}
                        </div>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Controls */}
                <div className="bg-white rounded-3xl p-6 shadow-xl border-2 border-slate-100 space-y-6">
                    <div>
                        <h3 className="font-bold text-slate-700 mb-3 flex items-center gap-2">
                            <Sigma className="text-amber-500" /> Aggregate Function
                        </h3>
                        <div className="space-y-2">
                            {FUNCTIONS.map(f => (
                                <button
                                    key={f.id}
                                    onClick={() => setSelectedFunc(f.id)}
                                    className={`w-full p-3 rounded-xl text-left border-2 transition-all ${selectedFunc === f.id ? 'border-amber-500 bg-amber-50' : 'border-slate-200 hover:border-amber-200'
                                        }`}
                                >
                                    <div className="font-mono font-bold text-sm">{f.label}</div>
                                    <div className="text-xs text-slate-500">{f.desc}</div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {selectedFunc !== 'COUNT' && (
                        <div>
                            <h3 className="font-bold text-slate-700 mb-3">Column</h3>
                            <div className="flex gap-2">
                                {(['amount', 'quantity'] as const).map(col => (
                                    <button
                                        key={col}
                                        onClick={() => setSelectedColumn(col)}
                                        className={`flex-1 py-2 rounded-xl font-bold border-2 ${selectedColumn === col ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-slate-200 text-slate-600'
                                            }`}
                                    >
                                        {col}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    <div>
                        <label className="flex items-center gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={groupBy}
                                onChange={e => setGroupBy(e.target.checked)}
                                className="w-5 h-5 accent-amber-500"
                            />
                            <span className="font-bold text-slate-700">GROUP BY category</span>
                        </label>
                    </div>

                    <button
                        onClick={runQuery}
                        disabled={missionComplete}
                        className={`w-full text-white py-3 rounded-xl font-bold flex items-center justify-center gap-2 transition-all ${missionComplete ? 'bg-slate-300 cursor-not-allowed' : 'bg-amber-500 hover:bg-amber-600'}`}
                    >
                        <Play size={18} /> Run Query
                    </button>
                </div>

                {/* Data Table */}
                <div className="bg-white rounded-3xl p-6 shadow-xl border-2 border-slate-100">
                    <h3 className="font-bold text-slate-700 mb-4 flex items-center gap-2">
                        <Hash className="text-blue-500" /> Sample Data (sales)
                    </h3>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-slate-50">
                                    <th className="px-3 py-2 text-left">id</th>
                                    <th className="px-3 py-2 text-left">category</th>
                                    <th className="px-3 py-2 text-right">amount</th>
                                    <th className="px-3 py-2 text-right">quantity</th>
                                </tr>
                            </thead>
                            <tbody>
                                {SAMPLE_DATA.map(row => (
                                    <tr key={row.id} className="border-t border-slate-100">
                                        <td className="px-3 py-2 text-slate-400">{row.id}</td>
                                        <td className="px-3 py-2">
                                            <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${row.category === 'Fruit' ? 'bg-green-100 text-green-700' :
                                                row.category === 'Vegetable' ? 'bg-blue-100 text-blue-700' :
                                                    'bg-red-100 text-red-700'
                                                }`}>{row.category}</span>
                                        </td>
                                        <td className="px-3 py-2 text-right font-mono">${row.amount}</td>
                                        <td className="px-3 py-2 text-right font-mono">{row.quantity}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Result */}
                <div className="space-y-6">
                    <div className="bg-slate-900 rounded-3xl p-6 shadow-xl">
                        <h3 className="font-bold text-green-400 mb-3">SQL Query</h3>
                        <pre className="font-mono text-sm text-green-400 whitespace-pre-wrap">
                            {getSqlQuery()}
                        </pre>
                    </div>

                    <div className={`bg-white rounded-3xl p-6 shadow-xl border-2 ${missionComplete ? 'border-green-400 bg-green-50' : 'border-amber-200'}`}>
                        <h3 className="font-bold text-slate-700 mb-3 flex items-center gap-2">
                            <TrendingUp className="text-amber-500" /> Result
                        </h3>
                        {result === null ? (
                            <div className="text-slate-400 italic">Run a query to see results...</div>
                        ) : (groupBy && Array.isArray(result)) ? (
                            <div className="space-y-2">
                                {(result as { category: string; result: number }[]).map((r, i) => (
                                    <div key={i} className="flex justify-between items-center p-3 bg-white/50 rounded-xl border border-amber-100">
                                        <span className="font-bold">{r.category}</span>
                                        <span className="font-mono text-xl text-amber-600">{r.result}</span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-4">
                                <div className="text-5xl font-black text-amber-600">{result}</div>
                                <div className="text-sm text-slate-500 mt-2">{selectedFunc}({selectedFunc === 'COUNT' ? '*' : selectedColumn})</div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// ============ MAIN COMPONENT ============
export const SqlAggregates = () => {
    const { completeModule } = useAcademyStore();
    const { playCourseTheme } = useMusicStore();
    const [completed, setCompleted] = useState(false);

    useEffect(() => {
        playCourseTheme('data-detective');
    }, []);

    const handleComplete = () => {
        setCompleted(true);
        completeModule('sql-aggregates', 100);
    };

    return (
        <LessonWrapper
            moduleName="SQL Aggregates"
            moduleEmoji="🧮"
            moduleColor="amber"
            lessons={LESSONS}
            completed={completed}
            activityLabel="Calculator"
        >
            <AggregateGame onComplete={handleComplete} />
        </LessonWrapper>
    );
};

export default SqlAggregates;
